import { Link } from "react-router-dom";
import { Disc3, CircleDot, Wrench, Zap, Power, Gauge, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { SectionHeading, Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";

const SERVICES = [
  {
    icon: Disc3,
    title: "Brake Replacement",
    text: "Our specialty. Front, rear or both axles — pads, rotors, calipers and hardware replaced at your home or workplace, backed by a 3-year / 36,000-mile warranty when we supply the parts.",
    to: "/brake-replacement",
    tag: "Most Popular",
  },
  {
    icon: CircleDot,
    title: "Wheel Bearing & Hub Replacement",
    text: "That highway hum or growl, diagnosed and fixed on site — bearings and complete hub assemblies.",
    to: "/wheel-bearing-hub-replacement",
  },
  {
    icon: Wrench,
    title: "Suspension Repair",
    text: "Struts, shocks, control arms, ball joints and sway links — built for New England potholes.",
    to: "/suspension-repair",
  },
  {
    icon: Zap,
    title: "Alternator Replacement",
    text: "Charging system tested first, then replaced where your car sits. No tow truck required.",
    to: "/alternator-replacement",
  },
  {
    icon: Power,
    title: "Starter Replacement",
    text: "Click but no crank? We test starter, battery and wiring, then fix the real problem on site.",
    to: "/starter-replacement",
  },
  {
    icon: Gauge,
    title: "Tune-Ups & Maintenance",
    text: "Spark plugs, filters, belts, batteries and scheduled maintenance — done in your driveway.",
    to: "/tune-ups-maintenance",
  },
];

export default function Services() {
  return (
    <>
      <SEO
        title="Mobile Mechanic Services | Brakes, Inspections & More — We Come to You"
        description="Mobile brake replacement, brake inspections, calipers, rotors and more — performed at your home or workplace across Middlesex County, MA and Southern NH."
        path="/services"
      />
      <section className="border-b border-edge">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <SectionHeading
            eyebrow="Our Services"
            title="Professional Repair, Delivered"
            sub="Brakes are what we do most — and do best. Every service below happens where your car is parked."
          />
        </div>
      </section>
      <section data-testid="services-grid" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.08}>
              <Link
                to={s.to}
                data-testid={`service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}-card`}
                className="group flex h-full flex-col border border-edge bg-slate2 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-crimson/60"
              >
                <div className="flex items-center justify-between">
                  <s.icon className="h-8 w-8 text-amber2" />
                  {s.tag && (
                    <span className="bg-crimson px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white">
                      {s.tag}
                    </span>
                  )}
                </div>
                <h2 className="mt-5 font-display text-2xl font-bold uppercase tracking-tight text-slate-50">{s.title}</h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{s.text}</p>
                <span className="mt-5 flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-crimson transition-colors group-hover:text-amber2">
                  Learn more <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
      <CTABand />
    </>
  );
}
